import React, { useState, useEffect } from 'react';
import { customerAPI, customerDetailAPI } from '../../services/api';
import './Customers.css';

const CustomerForm = ({ customerId, onClose, onSuccess }) => {
  const isEditMode = Boolean(customerId);

  const [formData, setFormData] = useState({
    fullname: '',
    phone: '',
    email: '',
    delivery_address: '',
    trailer_access: false,
  });

  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    fetchCustomFields();
    if (isEditMode) {
      fetchCustomer();
    }
  }, [customerId]);

  const fetchCustomFields = async () => {
    try {
      const response = await customerDetailAPI.getAll({ active_only: true });
      setCustomFields(response);
    } catch (err) {
      console.error('Error fetching custom fields:', err);
    }
  };

  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getById(customerId);
      setFormData({
        fullname: response.fullname || '',
        phone: response.phone || '',
        email: response.email || '',
        delivery_address: response.delivery_address || '',
        trailer_access: response.trailer_access || false,
      });

      // Load custom field values
      if (response.detail_values) {
        const values = {};
        response.detail_values.forEach((detailValue) => {
          values[detailValue.customer_detail_id] = detailValue.value;
        });
        setCustomFieldValues(values);
      }
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Failed to load customer');
      console.error('Error fetching customer:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setCustomFieldValues({
      ...customFieldValues,
      [fieldId]: value,
    });
    // Clear error for this field
    if (errors[`custom_field_${fieldId}`]) {
      setErrors({ ...errors, [`custom_field_${fieldId}`]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullname.trim()) {
      newErrors.fullname = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Validate required custom fields
    customFields.forEach((field) => {
      if (field.is_required && !customFieldValues[field.id]) {
        newErrors[`custom_field_${field.id}`] = `${field.name} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Prepare data - remove empty email if not provided
      const submitData = {
        ...formData,
        email: formData.email.trim() || null,
        custom_fields: customFieldValues,
      };

      if (isEditMode) {
        await customerAPI.update(customerId, submitData);
      } else {
        await customerAPI.create(submitData);
      }

      onSuccess();
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
      setSubmitError(
        err.response?.data?.message ||
        `Failed to ${isEditMode ? 'update' : 'create'} customer`
      );
      console.error('Error submitting form:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Customer' : 'Add New Customer'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {submitError && (
              <div className="error-message">{submitError}</div>
            )}

            {loading && isEditMode && !formData.fullname ? (
              <div className="loading">Loading customer...</div>
            ) : (
              <>
                <div className="form-section">
                  <h3>Personal Information</h3>
                  
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label htmlFor="fullname">
                        Full Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="fullname"
                        name="fullname"
                        value={formData.fullname}
                        onChange={handleChange}
                        className={errors.fullname ? 'error' : ''}
                        placeholder="Enter full name"
                      />
                      {errors.fullname && (
                        <span className="error-text">{errors.fullname}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">
                        Phone <span className="required">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={errors.phone ? 'error' : ''}
                        placeholder="Enter phone number"
                      />
                      {errors.phone && (
                        <span className="error-text">{errors.phone}</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={errors.email ? 'error' : ''}
                        placeholder="Enter email address (optional)"
                      />
                      {errors.email && (
                        <span className="error-text">{errors.email}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="form-section">
                  <h3>Delivery Information</h3>
                  
                    <div className="form-group">
                      <label htmlFor="delivery_address">Delivery Address</label>
                      <textarea
                        id="delivery_address"
                        name="delivery_address"
                        value={formData.delivery_address}
                        onChange={handleChange}
                        className={errors.delivery_address ? 'error' : ''}
                        placeholder="Enter delivery address"
                        rows="3"
                      />
                      {errors.delivery_address && (
                        <span className="error-text">{errors.delivery_address}</span>
                      )}
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          name="trailer_access"
                          checked={formData.trailer_access}
                          onChange={handleChange}
                        />
                        <span>Trailer Access Available</span>
                      </label>
                    </div>
                </div>

                {customFields.length > 0 && (
                  <div className="form-section">
                    <h3>Additional Information</h3>
                    
                    <div className="form-grid-2"> 
                      {customFields.map((field) => (
                        <div key={field.id} className="form-group">
                          <label htmlFor={`custom_field_${field.id}`}>
                            {field.name}
                            {field.is_required && <span className="required">*</span>}
                          </label>
                          {renderCustomField(field)}
                          {errors[`custom_field_${field.id}`] && (
                            <span className="error-text">{errors[`custom_field_${field.id}`]}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : isEditMode ? 'Update Customer' : 'Create Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  function renderCustomField(field) {
    const value = customFieldValues[field.id] || '';
    const fieldId = `custom_field_${field.id}`;
    const hasError = errors[fieldId];

    switch (field.field_type) {
      case 'textarea':
        return (
          <textarea
            id={fieldId}
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            className={hasError ? 'error' : ''}
            placeholder={`Enter ${field.name.toLowerCase()}`}
            rows="3"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            id={fieldId}
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            className={hasError ? 'error' : ''}
            placeholder={`Enter ${field.name.toLowerCase()}`}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            id={fieldId}
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            className={hasError ? 'error' : ''}
          />
        );

      case 'select':
        return (
          <select
            id={fieldId}
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            className={hasError ? 'error' : ''}
          >
            <option value="">Select an option</option>
            {field.options && field.options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                id={fieldId}
                checked={value === 'true' || value === true}
                onChange={(e) => handleCustomFieldChange(field.id, e.target.checked.toString())}
              />
              <span>Yes</span>
            </label>
          </div>
        );

      case 'text':
      default:
        return (
          <input
            type="text"
            id={fieldId}
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            className={hasError ? 'error' : ''}
            placeholder={`Enter ${field.name.toLowerCase()}`}
          />
        );
    }
  }
};

export default CustomerForm;
